import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EarningsDebug from "@/components/EarningsDebug";
import StoreStatus from "@/components/StoreStatus";
import { PlugsConnected, Plugs, ArrowsClockwise, PaperPlaneTilt, Warning, CheckCircle, Clock } from "@phosphor-icons/react";

export const DELIVERY_META = {
  delivering: { label: "Delivering", cls: "border-yellow-500 text-yellow-500" },
  submitted: { label: "In Too Lost QC", cls: "border-blue-400 text-blue-400" },
  live: { label: "Delivered", cls: "border-green-500 text-green-500" },
  failed: { label: "Delivery Failed", cls: "border-red-600 text-red-500" },
  takedown_pending: { label: "Takedown Pending", cls: "border-zinc-500 text-zinc-400" },
  takedown_complete: { label: "Taken Down", cls: "border-zinc-600 text-zinc-500" },
};

export function DeliveryPill({ delivery }) {
  if (!delivery?.status) return <span className="text-[9px] tracking-widest uppercase text-zinc-600">Not delivered</span>;
  const m = DELIVERY_META[delivery.status] || { label: delivery.status, cls: "border-zinc-600 text-zinc-400" };
  return <span className={`text-[9px] tracking-widest uppercase border px-1.5 py-0.5 ${m.cls}`} data-testid="delivery-pill">{m.label}{delivery.toolost_release_id ? ` · #${delivery.toolost_release_id}` : ""}</span>;
}

export default function AdminDistribution() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState({ redirect_uri: "", use_pkce: true });
  const [deliveries, setDeliveries] = useState([]);
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => {
    api.get("/toolost/status").then(({ data }) => { setStatus(data); setCfg({ redirect_uri: data.redirect_uri, use_pkce: data.use_pkce }); });
    api.get("/admin/deliveries").then(({ data }) => setDeliveries(data.deliveries));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = params.get("toolost");
    if (t === "connected") toast.success("Too Lost connected");
    if (t === "error") toast.error(`Too Lost connection failed: ${params.get("reason") || "unknown"}`);
    if (t) { params.delete("toolost"); params.delete("reason"); setParams(params, { replace: true }); }
  }, [params, setParams]);
  useEffect(() => {
    if (!deliveries.some(d => d.delivery?.status === "delivering")) return;
    const t = setInterval(load, 4000); return () => clearInterval(t);
  }, [deliveries]);

  const connect = async () => {
    try {
      await api.put("/toolost/settings", cfg);
      const { data } = await api.get("/toolost/connect");
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not start connection"); }
  };
  const disconnect = async () => { await api.post("/toolost/disconnect"); toast.success("Disconnected"); load(); };
  const sync = async (id) => {
    try { await api.post(`/admin/releases/${id}/delivery-sync`); toast.success("Synced with Too Lost"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };
  const retry = async (id) => {
    try { await api.post(`/admin/releases/${id}/deliver`); toast.success("Delivery restarted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not deliver"); }
  };
  const syncAll = async () => {
    try { const { data } = await api.post("/admin/sync/deliveries"); toast.success(`Checked ${data.checked} · ${data.changed} changed`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };
  const decideTakedown = async (id, approve) => {
    try {
      const { data } = await api.post(`/admin/releases/${id}/takedown`, { approve });
      toast.success(approve ? (data.via_api ? "Takedown sent to Too Lost" : "Takedown approved — reminder emailed to action in Too Lost portal") : "Takedown request rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pendingTakedowns = deliveries.filter(r => r.takedown?.status === "requested");

  return (
    <div data-testid="admin-distribution">
      <div className="border border-zinc-800 grid lg:grid-cols-12 mb-8">
        <div className="lg:col-span-5 p-6 border-r border-zinc-800">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Distribution Partner</div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 flex items-center justify-center border ${status?.connected ? "border-green-500 text-green-500" : "border-zinc-700 text-zinc-500"}`}>
              {status?.connected ? <PlugsConnected size={20} weight="duotone" /> : <Plugs size={20} weight="duotone" />}
            </div>
            <div>
              <div className="font-display text-xl uppercase tracking-tight">Too Lost</div>
              <div className="text-[10px] tracking-widest uppercase" data-testid="toolost-connection-status">
                {status === null ? <span className="text-zinc-500">Checking…</span> : status.connected ? <span className="text-green-500">● Connected{status.account?.username ? ` · ${status.account.username}` : ""}</span> : <span className="text-zinc-500">○ Not connected</span>}
              </div>
            </div>
          </div>
          {status?.connected ? (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500 leading-relaxed">Approved releases are automatically packaged and submitted to Too Lost. Statuses sync hourly; import Sales Report CSVs below for earnings. When Too Lost reports a release live, Autopilot flips it Live, features it in the Spotlight, and emails fans.</p>
              {status.account?.email && <div className="text-xs text-zinc-400">Account: {status.account.email} <span className="text-zinc-600">({status.account.type})</span></div>}
              <div className="flex flex-wrap gap-2">
                <button onClick={syncAll} data-testid="toolost-sync-all" className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 px-4 py-2 hover:border-white transition-colors inline-flex items-center gap-1"><ArrowsClockwise size={12} /> Sync Statuses</button>
                <button onClick={disconnect} data-testid="toolost-disconnect" className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 px-4 py-2 hover:border-red-600 hover:text-red-500 transition-colors">Disconnect</button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500 leading-relaxed">Connect your Too Lost account once. You'll be sent to Too Lost to log in and approve Asylum, then returned here.</p>
              <div className="border border-zinc-800 p-3 space-y-2" data-testid="toolost-settings">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Callback URI — must match your Too Lost app exactly</div>
                <input value={cfg.redirect_uri} onChange={(e) => setCfg({ ...cfg, redirect_uri: e.target.value })} data-testid="toolost-redirect-uri"
                  className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-red-600 focus:outline-none" />
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <input type="checkbox" checked={cfg.use_pkce} onChange={(e) => setCfg({ ...cfg, use_pkce: e.target.checked })} data-testid="toolost-use-pkce" className="accent-red-600" />
                  Send PKCE challenge (uncheck if Too Lost rejects the request)
                </label>
                <div className="text-[10px] text-zinc-600 font-mono">Client ID: {status?.client_id_hint}</div>
              </div>
              <button onClick={connect} data-testid="toolost-connect" className="bg-red-600 text-white px-5 py-3 text-[10px] tracking-[0.2em] uppercase btn-brutal btn-brutal-red inline-flex items-center gap-2"><PlugsConnected size={14} weight="bold" /> Save &amp; Connect Too Lost</button>
            </div>
          )}
        </div>
        <div className="lg:col-span-7 p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Delivery Timeline (typical)</div>
          <div className="grid grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 text-center">
            {[["Instant", "Asylum → Too Lost"], ["1–3 days", "Too Lost QC"], ["2–5 days", "Spotify / Apple live"], ["≤ 2 wks", "All other DSPs"]].map(([n, l]) => (
              <div key={l} className="bg-black p-4"><div className="font-display text-xl tracking-tight">{n}</div><div className="text-[9px] tracking-widest uppercase text-zinc-500 mt-1">{l}</div></div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">Business days. Advise artists to schedule releases at least 2–4 weeks out — Spotify needs ≥7 days before release date for Release Radar and editorial consideration.</p>
        </div>
      </div>

      <EarningsDebug />

      {pendingTakedowns.length > 0 && (
        <div className="border border-yellow-600/60 mb-8" data-testid="takedown-queue">
          <div className="px-6 py-3 border-b border-yellow-600/40 text-[10px] tracking-[0.2em] uppercase text-yellow-500 bg-yellow-600/5">Takedown Requests · {pendingTakedowns.length}</div>
          {pendingTakedowns.map(r => (
            <div key={r.id} className="flex items-center gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 text-sm" data-testid={`takedown-row-${r.id}`}>
              <div className="flex-1 min-w-0"><div className="truncate">{r.title} <span className="text-zinc-500">· {r.artist_name}</span></div><div className="text-xs text-zinc-400 mt-1">“{r.takedown.reason}”</div><div className="text-[10px] text-zinc-600 mt-1">{r.delivery?.toolost_release_id ? `Too Lost #${r.delivery.toolost_release_id}` : "Not delivered to Too Lost"} · requested {new Date(r.takedown.requested_at).toLocaleDateString()}</div></div>
              <button onClick={() => decideTakedown(r.id, true)} data-testid={`takedown-approve-${r.id}`} className="border border-red-600 text-red-500 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:bg-red-600 hover:text-white">Approve</button>
              <button onClick={() => decideTakedown(r.id, false)} data-testid={`takedown-reject-${r.id}`} className="border border-zinc-700 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-white">Reject</button>
            </div>
          ))}
        </div>
      )}

      <div className="border border-zinc-800 overflow-x-auto" data-testid="deliveries-table">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500 bg-zinc-950">
            <div className="col-span-4">Release</div><div className="col-span-2">Delivery</div><div className="col-span-2">Too Lost Status</div><div className="col-span-2">Last Sync</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {deliveries.length === 0 ? <div className="p-10 text-center text-zinc-500 text-sm">No deliveries yet. Approve a release to start one.</div> : deliveries.map(r => {
            const d = r.delivery || {};
            return (
              <div key={r.id} className="border-b border-zinc-800 last:border-b-0" data-testid={`delivery-row-${r.id}`}>
                <div className="grid grid-cols-12 gap-4 px-6 py-4 row-hover items-center text-sm">
                  <div className="col-span-4 truncate"><div>{r.title}</div><div className="text-[10px] text-zinc-500">{r.artist_name} · {r.release_type} · {r.release_date}</div></div>
                  <div className="col-span-2"><DeliveryPill delivery={d} /></div>
                  <div className="col-span-2 text-xs text-zinc-400">{d.toolost_status ? d.toolost_status.replace("_", " ") : "—"}{d.upc ? <div className="text-[10px] text-zinc-600 font-mono">UPC {d.upc}</div> : null}</div>
                  <div className="col-span-2 text-[10px] text-zinc-500 font-mono">{d.last_synced_at ? new Date(d.last_synced_at).toLocaleString() : "—"}</div>
                  <div className="col-span-2 flex justify-end gap-2">
                    {d.toolost_release_id && d.status !== "delivering" && <button onClick={() => sync(r.id)} data-testid={`delivery-sync-${r.id}`} title="Sync status" className="border border-zinc-700 px-2 py-1.5 text-xs hover:border-white"><ArrowsClockwise size={12} /></button>}
                    {d.status === "failed" && <button onClick={() => retry(r.id)} data-testid={`delivery-retry-${r.id}`} className="border border-red-600 text-red-500 px-2 py-1.5 text-[10px] tracking-widest uppercase hover:bg-red-600 hover:text-white inline-flex items-center gap-1"><PaperPlaneTilt size={12} /> Retry</button>}
                    <button onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`delivery-log-${r.id}`} className="border border-zinc-700 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-white">Log</button>
                  </div>
                </div>
                {open === r.id && (
                  <div className="px-6 pb-4 bg-zinc-950" data-testid={`delivery-log-body-${r.id}`}>
                    {(d.log || []).length === 0 ? <div className="text-xs text-zinc-600 py-2">No log entries.</div> : (d.log || []).map((l, i) => (
                      <div key={`${l.at}-${i}`} className="flex items-start gap-3 py-1.5 text-xs border-b border-zinc-900 last:border-b-0">
                        {l.level === "error" ? <Warning size={12} className="text-red-500 mt-0.5" /> : l.level === "success" ? <CheckCircle size={12} className="text-green-500 mt-0.5" /> : <Clock size={12} className="text-zinc-500 mt-0.5" />}
                        <span className="font-mono text-zinc-600 shrink-0">{new Date(l.at).toLocaleTimeString()}</span>
                        <span className={l.level === "error" ? "text-red-400" : "text-zinc-300"}>{l.msg}</span>
                      </div>
                    ))}
                    <StoreStatus release={r} onRefreshed={load} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
